import { getLocale, resolveLanguage } from './localeManifest.js';

const formatterCache = new Map();
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_COMPONENT_OPTIONS = [
  'weekday', 'era', 'year', 'month', 'day', 'dayPeriod',
  'hour', 'minute', 'second', 'fractionalSecondDigits', 'timeZoneName',
];

const cacheKey = (type, locale, options) => `${type}:${locale}:${JSON.stringify(
  Object.entries(options).sort(([left], [right]) => left.localeCompare(right)),
)}`;

const getFormatter = (type, Formatter, locale, options) => {
  const key = cacheKey(type, locale, options);
  if (!formatterCache.has(key)) formatterCache.set(key, new Formatter(locale, options));
  return formatterCache.get(key);
};

const toFiniteNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
};

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(DATE_ONLY_PATTERN);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const date = new Date(`${year}-${month}-${day}T00:00:00.000Z`);
      const valid = !Number.isNaN(date.getTime())
        && date.getUTCFullYear() === Number(year)
        && date.getUTCMonth() + 1 === Number(month)
        && date.getUTCDate() === Number(day);
      return valid ? { date, dateOnly: true } : null;
    }
  }

  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : { date, dateOnly: false };
};

const formatDateValue = (value, language, defaults, options) => {
  const parsed = toDate(value);
  if (!parsed) return '';

  const locale = getLocale(language);
  const formatterOptions = { ...defaults, ...options };
  if (DATE_TIME_COMPONENT_OPTIONS.some((key) => options[key] !== undefined)) {
    delete formatterOptions.dateStyle;
    delete formatterOptions.timeStyle;
  }
  if (parsed.dateOnly && !formatterOptions.timeZone) formatterOptions.timeZone = 'UTC';

  return getFormatter('date', Intl.DateTimeFormat, locale, formatterOptions).format(parsed.date);
};

export const formatDate = (value, language, options = {}) => formatDateValue(
  value,
  language,
  { dateStyle: 'medium' },
  options,
);

export const formatDateTime = (value, language, options = {}) => formatDateValue(
  value,
  language,
  { dateStyle: 'medium', timeStyle: 'short' },
  options,
);

export const formatNumber = (value, language, options = {}) => {
  const number = toFiniteNumber(value);
  if (number === null) return '';
  const locale = getLocale(language);
  return getFormatter('number', Intl.NumberFormat, locale, options).format(number);
};

export const formatCurrency = (value, currency, language, options = {}) => {
  const number = toFiniteNumber(value);
  if (number === null || typeof currency !== 'string' || !currency.trim()) return '';
  return formatNumber(number, language, {
    style: 'currency',
    currency: currency.toUpperCase(),
    ...options,
  });
};

export const formatRelativeTime = (value, unit, language, options = {}) => {
  const number = toFiniteNumber(value);
  if (number === null || typeof unit !== 'string' || !unit) return '';
  const locale = getLocale(language);
  return getFormatter(
    'relativeTime',
    Intl.RelativeTimeFormat,
    locale,
    { numeric: 'auto', ...options },
  ).format(number, unit);
};

export const formatList = (values, language, options = {}) => {
  if (!Array.isArray(values)) return '';
  const items = values.filter((value) => value !== null && value !== undefined).map(String);
  const locale = getLocale(language);
  return getFormatter(
    'list',
    Intl.ListFormat,
    locale,
    { style: 'long', type: 'conjunction', ...options },
  ).format(items);
};

export const compareLocalized = (left, right, language, options = {}) => {
  const locale = getLocale(language);
  return getFormatter('collator', Intl.Collator, locale, options).compare(
    String(left ?? ''),
    String(right ?? ''),
  );
};

export const createLocaleFormatters = (language) => {
  const resolvedLanguage = resolveLanguage(language);
  return Object.freeze({
    language: resolvedLanguage,
    locale: getLocale(resolvedLanguage),
    date: (value, options) => formatDate(value, resolvedLanguage, options),
    dateTime: (value, options) => formatDateTime(value, resolvedLanguage, options),
    number: (value, options) => formatNumber(value, resolvedLanguage, options),
    currency: (value, currency, options) => formatCurrency(value, currency, resolvedLanguage, options),
    relativeTime: (value, unit, options) => formatRelativeTime(value, unit, resolvedLanguage, options),
    list: (values, options) => formatList(values, resolvedLanguage, options),
    compare: (left, right, options) => compareLocalized(left, right, resolvedLanguage, options),
  });
};
